import { FC, useMemo, useState, useRef } from 'react';
import dayjs from 'dayjs';
import { TaskItem, TaskItemDuration } from '@demind-inc/core';
import clsx from 'clsx';
import { DateTimePicker } from '@mui/x-date-pickers';
import { ClickAwayListener, CircularProgress, TextField } from '@mui/material';
import { mdiCalendarMonthOutline, mdiCreation } from '@mdi/js';
import Icon from '@mdi/react';
import { useTranslation } from 'react-i18next';

import { DateTimeSetOnlyTime } from '../TaskDueDatePicker';
import { DurationField } from '../../common';
import { TaskTimeWithSuggestion } from '../../KanbanView/AutoTimeSuggestion';
import './TaskScheduleSection.scss';
import { useTaskTimeSuggestion } from '../../../hooks';
import { getDueDateYYYYMMDD } from '../../../helpers';
import { formatDataDate } from '../../DataView/i18n';
import { TaskPropertyRow } from '../TaskPropertyRow';

interface TaskScheduleSectionProps {
  task?: Partial<TaskItem>;
  dueDateTime?: {
    datetime?: string;
    date?: string;
    timezone?: string;
  };
  dateSet: DateTimeSetOnlyTime;
  duration: TaskItemDuration;
  disabled?: boolean;
  onDateChange: (dateSet: DateTimeSetOnlyTime) => void;
  onClearDate?: () => void;
  onClearTime?: () => void;
  onDurationChange: (duration: TaskItemDuration) => void;
  timeFormat?: '12h' | '24h';
  phase?: {
    label?: string;
    color?: string;
  } | null;
  taskTime?: string;
  variant?: 'panel' | 'inline';
  hideSmartScheduleButton?: boolean;
}

export const TaskScheduleSection: FC<TaskScheduleSectionProps> = ({
  task,
  dueDateTime,
  dateSet,
  duration,
  disabled,
  onDateChange,
  onClearTime,
  onDurationChange,
  timeFormat = '12h',
  phase,
  variant = 'panel',
  hideSmartScheduleButton = false,
}) => {
  const { t, i18n } = useTranslation();
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [timePickerOpen, setTimePickerOpen] = useState(false);
  const datePickerAnchorRef = useRef<HTMLDivElement>(null);

  const selectedDate = useMemo(() => {
    return dateSet?.date || getDueDateYYYYMMDD(dueDateTime) || dayjs().format('YYYY-MM-DD');
  }, [dateSet?.date, dueDateTime]);

  const selectedTime = useMemo(
    () => (dateSet?.datetime ? dayjs(dateSet.datetime) : null),
    [dateSet?.datetime]
  );

  const taskForSuggestion = useMemo(() => {
    const baseDueDateTime = task?.dueDateTime ?? {};
    return {
      ...task,
      duration,
      dueDateTime: {
        ...baseDueDateTime,
        datetime: dateSet?.datetime,
        date: dateSet?.date || selectedDate,
        timezone: dueDateTime?.timezone ?? baseDueDateTime?.timezone,
      },
    };
  }, [dateSet?.date, dateSet?.datetime, dueDateTime?.timezone, duration, selectedDate, task]);

  const {
    requestSuggestion,
    clearSuggestion,
    isPending: isFindingBestTime,
    canRequestSuggestion,
    suggestedTime,
    suggestionExplanation,
  } = useTaskTimeSuggestion({
    date: selectedDate,
    task: taskForSuggestion,
    suggestionKey: (taskForSuggestion as any)?.taskId ?? (taskForSuggestion as any)?.originalTaskId,
  });

  const getDateDisplayValue = (date: dayjs.Dayjs | null): string => {
    if (!date?.isValid()) return '';

    const dateInstance = date;
    const today = dayjs();
    const isToday = dateInstance.isSame(today, 'day');
    const isTomorrow = dateInstance.isSame(today.add(1, 'day'), 'day');

    if (isToday) return t('task.sections.today');
    if (isTomorrow) return t('task.sections.tomorrow');

    return formatDataDate(i18n.language, dateInstance.toDate(), {
      month: 'short',
      day: 'numeric',
    });
  };

  const getDatePickerValue = () => {
    if (dateSet?.datetime) {
      return dayjs(dateSet.datetime);
    }
    if (dateSet?.date) {
      return dayjs(dateSet.date);
    }
    return null;
  };

  const handleDateChange = (newValue: dayjs.Dayjs | null) => {
    if (newValue && dayjs(newValue).isValid()) {
      const currentTime = dateSet?.datetime ? dayjs(dateSet.datetime) : dayjs();
      const combinedDateTime = newValue.hour(currentTime.hour()).minute(currentTime.minute());

      onDateChange({
        datetime: dateSet?.datetime ? combinedDateTime.toISOString() : undefined,
        date: newValue.format('YYYY-MM-DD'),
      });
    }
    setDatePickerOpen(false);
  };

  const handleTimeChange = (time: dayjs.Dayjs | null) => {
    if (time && dayjs(time).isValid()) {
      const currentDate = dateSet?.datetime
        ? dayjs(dateSet.datetime)
        : dateSet?.date
          ? dayjs(dateSet.date)
          : dayjs();

      const combinedDateTime = currentDate.hour(time.hour()).minute(time.minute());

      onDateChange({
        datetime: combinedDateTime.toISOString(),
        date: dateSet?.date || currentDate.format('YYYY-MM-DD'),
      });
    } else {
      onClearTime?.();
    }
    setTimePickerOpen(false);
  };

  const handleClickAway = () => {
    setDatePickerOpen(false);
  };

  const dateValue = getDatePickerValue();
  const displayValue = getDateDisplayValue(dateValue);

  return (
    <div
      className={clsx(
        'task-schedule-section',
        variant === 'inline' && 'task-schedule-section--inline'
      )}
    >
      <ClickAwayListener onClickAway={handleClickAway}>
        <div className="task-schedule-section__date-block">
          <TaskPropertyRow
            icon={<Icon path={mdiCalendarMonthOutline} className="task-detail-modal__icon" />}
            action={
              <div className="task-schedule-section__date-actions">
                <DurationField
                  value={duration}
                  onChange={onDurationChange}
                  showIcon={false}
                  disabled={disabled}
                  className="task-schedule-section__duration-pill"
                  shortFormat
                />
              </div>
            }
          >
            <div ref={datePickerAnchorRef} className="task-schedule-section__date-value">
              <DateTimePicker
                open={datePickerOpen}
                onClose={() => setDatePickerOpen(false)}
                format="MMM D"
                value={dateValue}
                views={['month', 'day']}
                onAccept={handleDateChange}
                onChange={handleDateChange}
                disabled={disabled}
                slots={{
                  textField: (params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      className="task-schedule-section__date-input"
                      onClick={disabled ? undefined : () => setDatePickerOpen(true)}
                      disabled={disabled}
                      InputProps={{
                        ...params.InputProps,
                        disableUnderline: true,
                      }}
                      value={displayValue}
                      inputProps={{
                        ...params.inputProps,
                        readOnly: true,
                      }}
                    />
                  ),
                  actionBar: () => null,
                }}
                slotProps={{
                  openPickerIcon: {
                    style: { display: 'none' },
                  },
                  popper: {
                    className: clsx(
                      'custom-time-picker-popper',
                      'custom-time-picker-popper--auto-height'
                    ),
                    anchorEl: datePickerAnchorRef.current,
                    placement: 'bottom-start',
                  },
                }}
              />
            </div>
          </TaskPropertyRow>

          <div className="task-schedule-section__time-meta">
            <TaskTimeWithSuggestion
              date={selectedDate}
              value={selectedTime}
              onChange={handleTimeChange}
              task={taskForSuggestion}
              timeFormat={timeFormat}
              disabled={disabled}
              isPending={isFindingBestTime}
              canRequestSuggestion={canRequestSuggestion}
              suggestedTime={suggestedTime}
              suggestionExplanation={suggestionExplanation}
              onClearSuggestion={clearSuggestion}
              onFindBestTime={async () => {
                const next = await requestSuggestion();
                if (next) handleTimeChange(next);
              }}
              className="task-schedule-section__time-wrapper"
              selectorClassName={clsx(
                'task-schedule-section__time-chip',
                !selectedTime && !timePickerOpen && 'task-schedule-section__time-chip--empty'
              )}
              autocompleteClassName="task-schedule-section__time-selector"
              open={timePickerOpen}
              setOpen={setTimePickerOpen}
              placeholder={t('task.modal.pickATime')}
              defaultTimeInSelector={selectedTime || dayjs()}
              showClearButton={!disabled && !!dateSet?.datetime}
              showFindBestTime={false}
              showConnector={false}
            />

            {!selectedTime && !hideSmartScheduleButton && (
              <>
                <div className="task-schedule-section__time-meta__connector" />
                <button
                  type="button"
                  className="task-schedule-section__method task-schedule-section__method--smart"
                  disabled={disabled || isFindingBestTime || !canRequestSuggestion}
                  onClick={async () => {
                    const next = await requestSuggestion();
                    if (next) handleTimeChange(next);
                  }}
                >
                  {isFindingBestTime ? (
                    <CircularProgress
                      size={14}
                      className="task-schedule-section__method__spinner"
                    />
                  ) : (
                    <Icon
                      path={mdiCreation}
                      size="14px"
                      className="task-schedule-section__method__sparkle"
                    />
                  )}
                  <span>{t('task.modal.findBestTime')}</span>
                </button>
              </>
            )}

            {phase?.label && (
              <span
                className="task-schedule-section__phase"
                style={{ backgroundColor: phase.color }}
              >
                {phase.label}
              </span>
            )}
          </div>
        </div>
      </ClickAwayListener>
    </div>
  );
};
